import type { CameraPose } from '../../data/types';
import type { Vec2 } from '../../geo/types';
import { residualWarp as committedSection } from '../../data/painterView.json';
import {
  derivePainterProjectionParams,
  STYLE_CONSTANTS,
  warpPosition,
} from '../../render/painterProjection';
import {
  painterFrameFromView,
  painterFrameShift,
  projectToPainterFrame,
  verticalShiftPx,
  WARP_FADE_DEPTH_M,
  WARP_LIMIT_PX,
  type PainterResidualWarp,
} from '../../render/painterWarp';
import { committedResidualWarp } from '../../render/painterWarpData';
import { createFarArc } from './farArc';
import {
  createColumnShiftBound,
  createShiftBound,
  renderedBoxOf,
  type ChordBound,
  type PixelBox,
  type ShiftBound,
} from './shiftBound';

/**
 * The painter view at full amount moves every vertex through the forward compression and then by the
 * residual shift, along its painting-frame row and down its column, and the rasteriser draws each
 * edge straight between its moved ends. `bendPx` bounds how far the moved image of the edge from a to
 * b strays from that straight line, in painting-frame pixels.
 */
export interface PainterBend {
  bendPx(a: Vec2, b: Vec2): number;
  // The same between vertices i and j of a working buffer [x0, y0, x1, y1, ...] that only grows,
  // moving each vertex once.
  overVertices(positions: readonly number[]): (i: number, j: number) => number;
}

// Where the painter view renders a vertex before the residual shift, and the shift it draws it with.
interface Drawn {
  readonly pixel: Vec2;
  readonly shift: Vec2;
}

interface CommittedShift {
  readonly warp: PainterResidualWarp;
  readonly shown: PixelBox;
  readonly bound: ShiftBound;
  readonly columns: ShiftBound;
}

// The committed warp is fixed, so the bound over it is built once, on first use.
let committed: CommittedShift | null = null;

function shiftOver(warp: PainterResidualWarp): CommittedShift {
  const shown = renderedBoxOf(warp, committedSection.canvasPx as unknown as PixelBox);
  const [u, v] = [STYLE_CONSTANTS.imageWidth / 2, STYLE_CONSTANTS.imageHeight / 2];
  const reach: PixelBox = [
    [u - WARP_LIMIT_PX, v - WARP_LIMIT_PX],
    [u + WARP_LIMIT_PX, v + WARP_LIMIT_PX],
  ];
  return {
    warp,
    shown,
    bound: createShiftBound(warp, shown, reach),
    columns: createColumnShiftBound(warp, shown, reach),
  };
}

function committedShift(): CommittedShift {
  committed ??= shiftOver(committedResidualWarp());
  return committed;
}

function lerp(a: Vec2, b: Vec2, t: number): Vec2 {
  return [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])];
}

// How far the box around p and q lies from `box`, along the axis it lies farthest on; zero or less
// where they overlap.
function gapTo([p, q]: readonly [Vec2, Vec2], [min, max]: PixelBox): number {
  return Math.max(
    min[0] - Math.max(p[0], q[0]),
    Math.min(p[0], q[0]) - max[0],
    min[1] - Math.max(p[1], q[1]),
    Math.min(p[1], q[1]) - max[1]
  );
}

// The painter view on one plane: where it renders a layout point, where it draws one it moves, at
// what depth the point itself lies, and how far the far map bends the edge between two points from
// the chord between where it renders them, refined until `settle` is decided (farArc).
interface Painter {
  readonly renderedAt: (p: Vec2) => Vec2;
  readonly drawnAt: (p: Vec2) => Drawn | null;
  readonly depthAt: (p: Vec2) => number;
  readonly arcOf: (a: Vec2, b: Vec2, chord: readonly [Vec2, Vec2], settle: number) => number;
  readonly shift: CommittedShift;
  readonly limitPx: number;
}

function painterOn(
  view: CameraPose,
  planeY: number,
  limitPx: number,
  shift: CommittedShift
): Painter {
  const derived = derivePainterProjectionParams(view, STYLE_CONSTANTS);
  const frame = painterFrameFromView(view, STYLE_CONSTANTS);
  const moved = (p: Vec2) => warpPosition([p[0], planeY, -p[1]], derived, 1);
  const renderedAt = (p: Vec2) => projectToPainterFrame(frame, moved(p)).pixel;
  const farArc = createFarArc(view, planeY, renderedAt);
  return {
    renderedAt,
    drawnAt(p) {
      const position = moved(p);
      const { pixel, depth } = projectToPainterFrame(frame, position);
      if (depth < WARP_FADE_DEPTH_M) return null;
      return { pixel, shift: painterFrameShift(frame, shift.warp, position, 1) };
    },
    depthAt: (p) => projectToPainterFrame(frame, [p[0], planeY, -p[1]]).depth,
    arcOf: (a, b, chord, settle) => farArc.arcPx(a, b, chord, settle),
    shift,
    limitPx,
  };
}

// How far a component of the shift strays from its linear interpolation over a chord λ px long: at
// most λ²κ/8 + λk/4, κ and k bounding its curvature and the turns the spline centres add.
function strayOf(span: number, { curvature, kink }: ChordBound): number {
  return (span * span * curvature) / 8 + (span * kink) / 4;
}

// The column shift's curvature bound, the largest over every direction, is set by its lever's term
// 2 h_v / s down the columns, which a chord along the rows does not feel. Where the bound over the
// whole chord passes the limit, the column shift is sampled at the ends of pieces short enough that
// over one it strays from its interpolation by at most PIECE_STRAY_PX.
const PIECE_STRAY_PX = 0.05;
const MAX_PIECES = 32;

function piecesFor(span: number, columns: ChordBound): number {
  const pieces = Math.ceil(span * Math.sqrt(columns.curvature / (8 * PIECE_STRAY_PX)));
  return Math.min(MAX_PIECES, Math.max(1, pieces));
}

// How far the column shift strays from its interpolation along the rendered chord from pa to pb: its
// largest stray at the ends of `pieces` equal pieces, and by strayOf over a piece between them.
function columnStray(
  warp: PainterResidualWarp,
  [pa, pb]: readonly [Vec2, Vec2],
  columns: ChordBound,
  pieces: number
): number {
  const between = strayOf(Math.hypot(pb[0] - pa[0], pb[1] - pa[1]) / pieces, columns);
  const [sa, sb] = [verticalShiftPx(warp, pa), verticalShiftPx(warp, pb)];
  const sampled = Array.from({ length: pieces - 1 }, (_, i) => {
    const t = (i + 1) / pieces;
    return Math.abs(verticalShiftPx(warp, lerp(pa, pb, t)) - sa - t * (sb - sa));
  });
  return Math.max(0, ...sampled) + between;
}

// The shift moves the arc along rows and down columns: over its chord each component strays from its
// linear interpolation by strayOf, and by up to its slope where the canvas shows the arc times the
// arc's distance more; of a move along rows only the part across the drawn chord counts, |n_u| of it,
// and of a move down columns |n_v|. The arc is refined only until it is plain whether the bend passes
// the limit, and an arc from a chord off the canvas until it is plain whether it reaches the canvas.
// An unbounded shift over an arc that is straight multiplies out to NaN, which bounds nothing.
function bendBetween(painter: Painter, a: Vec2, b: Vec2, da: Drawn, db: Drawn): number {
  const chord = [da.pixel, db.pixel] as const;
  const [du, dv] = [db.pixel[0] - da.pixel[0], Math.abs(db.pixel[1] - da.pixel[1])];
  if (du === 0 && dv === 0) return 0;
  const gap = gapTo(chord, painter.shift.shown);
  if (gap > 0 && painter.arcOf(a, b, chord, gap) < gap) return 0;
  const rows = painter.shift.bound.along(da.pixel, db.pixel);
  const columns = painter.shift.columns.along(da.pixel, db.pixel);
  const drawnU = du + db.shift[0] - da.shift[0];
  const drawnV = Math.abs(db.pixel[1] + db.shift[1] - (da.pixel[1] + da.shift[1]));
  const drawnSpan = Math.hypot(drawnU, drawnV);
  const acrossRows = drawnSpan > 0 ? drawnV / drawnSpan : 1;
  const acrossColumns = drawnSpan > 0 ? Math.abs(drawnU) / drawnSpan : 1;
  const span = Math.hypot(du, dv);
  const stretch = 1 + rows.shownSlope * acrossRows + columns.shownSlope * acrossColumns;
  const totalWith = (stray: number) => {
    const shifted = acrossRows * strayOf(span, rows) + acrossColumns * stray;
    const arc = painter.arcOf(a, b, chord, (painter.limitPx - shifted) / stretch);
    return arc * stretch + shifted;
  };
  const whole = totalWith(strayOf(span, columns));
  const bend =
    whole <= painter.limitPx
      ? whole
      : totalWith(columnStray(painter.shift.warp, chord, columns, piecesFor(span, columns)));
  return Number.isNaN(bend) ? Infinity : bend;
}

// The eye stands 2 km up, so the canvas shows no ground nearer than 2 km and the fade depth lies behind
// the painter's foot, where the compression leaves the ground as it is and depth runs linearly along
// an edge. An edge from nearer than that is split while the arc its farther part draws may reach the
// canvas.
function leavingFade(painter: Painter, near: Vec2, far: Vec2, drawn: Drawn): number {
  const [dn, df] = [painter.depthAt(near), painter.depthAt(far)];
  const start = lerp(near, far, (WARP_FADE_DEPTH_M - dn) / (df - dn));
  const chord = [painter.renderedAt(start), drawn.pixel] as const;
  const gap = gapTo(chord, painter.shift.shown);
  return gap > 0 && painter.arcOf(start, far, chord, gap) < gap ? 0 : Infinity;
}

function bendFrom(painter: Painter, a: Vec2, b: Vec2, da: Drawn | null, db: Drawn | null): number {
  if (da && db) return bendBetween(painter, a, b, da, db);
  if (da) return leavingFade(painter, b, a, da);
  return db ? leavingFade(painter, a, b, db) : 0;
}

/**
 * The bend of the painter view at `view` for edges on the plane at `planeY`, over the canvas every
 * supported window shows there, beyond `limitPx`, the bend the caller tolerates, only as a bound.
 * The residual warp is the committed one unless `warp` is given.
 */
export function createPainterBend(
  view: CameraPose,
  planeY: number,
  limitPx: number,
  warp?: PainterResidualWarp
): PainterBend {
  const shift = warp ? shiftOver(warp) : committedShift();
  const painter = painterOn(view, planeY, limitPx, shift);
  // Height tiles repeat the same boundary vertices in separate working buffers. Keep their exact
  // projection across those buffers without letting a far-country build retain every sampled point.
  const projected = new Map<number, Map<number, Drawn | null>>();
  let cachedPoints = 0;
  const drawnAt = (p: Vec2): Drawn | null => {
    let column = projected.get(p[0]);
    const known = column?.get(p[1]);
    if (known !== undefined) return known;
    const drawn = painter.drawnAt(p);
    if (cachedPoints >= 16_384) {
      projected.clear();
      cachedPoints = 0;
      column = undefined;
    }
    if (!column) {
      column = new Map();
      projected.set(p[0], column);
    }
    column.set(p[1], drawn);
    cachedPoints++;
    return drawn;
  };
  return {
    bendPx: (a, b) => bendFrom(painter, a, b, painter.drawnAt(a), painter.drawnAt(b)),
    overVertices(positions) {
      const drawn: (Drawn | null)[] = [];
      const points: Vec2[] = [];
      const at = (i: number): Vec2 => (points[i] ??= [positions[i * 2]!, positions[i * 2 + 1]!]);
      const drawnOf = (i: number) => {
        if (drawn[i] === undefined) drawn[i] = drawnAt(at(i));
        return drawn[i];
      };
      return (i, j) => bendFrom(painter, at(i), at(j), drawnOf(i), drawnOf(j));
    },
  };
}
